import React, {useContext, useEffect, useState} from 'react';
import {injectIntl} from "react-intl";
import { useDataLayer } from "dataLayerHelper";
import ListItem from "@Components/ListItem";
import Loading from "@Components/Loading";
import SectionHeader from "@Components/SectionHeader";
import Modal from "@Components/Modal";
import Button from "@FormElements/Button";
import Form from 'form';
import FormContext from "formContext";
import axios from "axios";
import Logger from '@ffw/logger-lib';
import {Helmet} from "rnd-helmet";
import SubMenu from "@Components/SubMenu";
import useReplaceInitials from "useReplaceInitials";
import PersonalDataInfo from "@Components/PersonalDataInfo";

function ProfilePersonalDetails({intl, staticContext, match}) {
  const {
    formData,
    setFormData,
    handleErrors,
    currentStep,
    getFormSchema,
    showToast,
    setToastMessage,
    setErrors,
    additionalData,
    setAdditionalData,
    timesRendered,
    triggerRerender,
    handleChange
  } = useContext(FormContext);

  const {user} = additionalData;
  const {dsarSettings} = additionalData;
  const {locale} = intl;
  const personalDetailsForm = "personal-details", contactDetailsForm = "contact-details";
  const cvDetailsForm = "cv-details";
  const experienceDetailsForm = "work-xp";
  const userGraduationForm = "user-education-details";
  const userLanguageForm = 'skills'
  const [formName, setFormName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Generate dataLayer for the page.
  useDataLayer(locale, staticContext);

  useEffect(() => {
    if (window.__ROUTE_DATA__) {
      delete window.__ROUTE_DATA__;
    }
    else {
      axios.get(`${process.env.REACT_APP_API_PREFIX}/user-data?currentLanguage=${locale}`)
        .then(response => {
          if (response.data.redirect === true && response.data.location) {
            window.location.href = response.data.location;
          }
          else {
            setAdditionalData(prevState => ({
              ...prevState,
              ...response.data
            }));
          }
        });
    }
  }, [timesRendered]);

  if (!user) {
    return <Loading/>
  }

  const handleSubmit = event => {
    event.preventDefault();

    setLoading(true);
    let submitApiPath;
    switch (formName) {
      case personalDetailsForm:
        submitApiPath = '/user/personal-details/';
        break;
      case contactDetailsForm:
        submitApiPath = '/user/contact-details/';
        break;
      case experienceDetailsForm:
        submitApiPath = '/user/work-xp/';
        break;
      case userLanguageForm:
        submitApiPath = '/user/skills/';
        break;
      case userGraduationForm:
        submitApiPath = '/user/education-details/';
        break;
      case cvDetailsForm:
        submitApiPath = '/user/resume/';
        break;
    }
    if (!submitApiPath) {
      Logger.info(`Unable to update data. Missing API path for ${formName} form`, "Profile/profile-personal-details/tr");
      return null;
    }

    axios.put(`${process.env.REACT_APP_API_PREFIX}${submitApiPath}`, formData[currentStep])
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          if (window && window.initAuthWidget && window.myRandClient) {
            // Name might have changed. Fetch refresh-token, which will also update MyRandInfo cookie, and
            // then replace user names and initials in the header.
            const replaceInitials = useReplaceInitials(formData[currentStep]);
            window.myRandClient.refreshToken(replaceInitials);
          }
          setModalOpen(false);
          setFormData([]);
          setErrors();
          setLoading(false);
          setTimeout(() => {
            const message = intl.formatMessage({id: 'Profile.AccountUpdated.Toast.SuccessMessage'});
            setToastMessage(message);
            showToast();
          }, 500);
          triggerRerender();
        }
      })
      .catch(error => {
        setLoading(false);
        handleErrors(error.response.data);
      });
  };

  const onCloseModal = () => {
    setFormData([]);
    setErrors();
    setFormName('');
    setModalOpen(false)
  };

  const handleEditAboutMe = () => {
    const itemProps = ['first_name', 'last_name', 'date_of_birth', 'register_region','accept_email_alert'];
    let data = {};
    itemProps.forEach(key => user[key] !== undefined ? data[key] = user[key] : false);

    setFormData([data]);
    setFormName(personalDetailsForm);
    setModalOpen(true);
  };

  const handleEditContactDetails = () => {
    const itemProps = ['phone', 'email'];
    let data = {};
    itemProps.forEach(key => user[key] !== undefined ? data[key] = user[key] : false);
    setFormData([data]);
    setFormName(contactDetailsForm);
    setModalOpen(true);
  };
  const handleEditExperienceDetails = () => {
    const itemProps = ['seniority', 'last_job', 'last_employer', 'industry', 'job_function', 'job_experience'];
    let data = {};
    itemProps.forEach(key => user[key] !== undefined ? data[key] = user[key] : false);
    setFormData([data]);
    setFormName(experienceDetailsForm);
    setModalOpen(true);
  }
  const handleEditLanguagesDetails = () => {
    const itemProps = ['language1', 'language1_level', 'language2', 'language2_level', 'language3', 'language3_level'];
    let data = {};
    itemProps.forEach(key => user[key] !== undefined ? data[key] = user[key] : false);
    setFormData([data]);
    setFormName(userLanguageForm);
    setModalOpen(true);

  }
  const handleEditEducationDetails = () => {
    const itemProps = ['education', 'last_school'];
    let data = {};
    itemProps.forEach(key => user[key] !== undefined ? data[key] = user[key] : false);
    setFormData([data]);
    setFormName(userGraduationForm);
    setModalOpen(true);

  }

  const handleEditCVDetails = () => {
    let data = {};

    if (!!user.CV) {
      data = {
        files: [user.CV],
        CV: user.CV,
      };

      if (data.CV) {
        if (!data.CV.size) {
          data.CV.size = 1;
        }
      }
    } else {
      data.CV = {};
      data.CV.delete = true;
    }

    setFormData([data]);
    setFormName(cvDetailsForm);
    setModalOpen(true);
  };


  const modalFooter = () => {
    const modalButtonSettings = {
      medium: true,
      filled: true,
      fullWidth: true,
      text: getFormSchema(formName)[currentStep].modalButton,
      loading
    }

    return <Button onClick={handleSubmit} settings={modalButtonSettings} />
  }

  const renderModal = () => {
    if (!modalOpen || !formName) {
      return '';
    }

    return (
      <Modal
        title={intl.formatMessage({id: getFormSchema(formName)[currentStep].modalTitle})}
        onClose={onCloseModal}
        footer={modalFooter()}
        footerDivider={true}
      >
        <Form name={formName} handleChange={handleChange} state={formData[currentStep]} handleSubmit={handleSubmit}/>
      </Modal>
    );
  };

  const renderPhoto = () => {
    if (!user || !user.photo || !user.photo.url) {
      return '';
    }
    return (
      <div className="profile__photo">
        <img alt="profile photo" src={`${user.photo.url}`}/>
      </div>
    );
  };

  const userCv = () => {
    if (user.CV) {
      return (
        <>
          <div className="my-environment-item__list my-environment-item__list-details mt-s">
            <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.CV.File.Label'})}</div>
            <div className="my-environment-item__list-content mt-xxs"><a href="#" rel="noopener noreferrer" onClick={e => e.preventDefault()}>{user.CV.filename}</a></div>
          </div>
        </>
      );
    }
    else {
      return null;
    }
  }
  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({id: 'Menu.Profile'})} | {intl.formatMessage({id: 'Breadcrumb.Dashboard'})}</title>
        <header className="header bg-variant-white header--s header--welcome"/>
      </Helmet>
      <SubMenu />
      <div className="block__wrapper wrapper l:mt-m">
        <SectionHeader title={intl.formatMessage({id: 'Profile.PersonalDetails.Section.Title'})}/>
        <div className="block__content block__content--xs block__content--align-right">
          <ul className="list-items">
            <ListItem
              title={intl.formatMessage({id: 'Profile.PersonalDetails.AboutMe.List.Title'})}
              handleEdit={handleEditAboutMe}
              label={intl.formatMessage({id: 'Profile.PersonalDetails.AboutMe.Modal.Title'})}
              divider="true"
            >
              {renderPhoto()}
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.AboutMe.FirstName.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.first_name}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.AboutMe.LastName.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.last_name}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.AboutMe.BirthDay.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.date_of_birth}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.AboutMe.Region.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{intl.formatMessage({id: `SelectField.RegisterRegion.${user.register_region}`})}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Subs.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.accept_email_alert === "yes" ? intl.formatMessage({id: 'User.Profile.Accept.EmailAlers.Yes'}) : intl.formatMessage({id: 'User.Profile.Accept.EmailAlers.No'})}</div>
              </div>
            </ListItem>
            <ListItem
              title={intl.formatMessage({id: 'Profile.PersonalDetails.ContactDetails.List.Title'})}
              handleEdit={handleEditContactDetails}
              label={intl.formatMessage({id: 'Profile.PersonalDetails.ContactDetails.Modal.Title'})}
              divider="true"
            >
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.ContactDetails.Phone.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.phone}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.PersonalDetails.ContactDetails.Email.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.email}</div>
              </div>
            </ListItem>
            <ListItem
              title={intl.formatMessage({id: 'Profile.Experience.Details.List.Title'})}
              handleEdit={handleEditExperienceDetails}
              label={intl.formatMessage({id: 'Profile.Experience.Details.Modal.Title'})}
              divider="true"
            >
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.Register.SelectSeniority.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{intl.formatMessage({id: `SelectField.RegisterSeniority.${user.seniority}`})}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Register.Form.CurrentLastJob.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.last_job}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Register.Form.CurrentLastEmployer.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.last_employer}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.Register.Industry.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{intl.formatMessage({id: `SelectField.Industry.${user.industry}`})}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.Register.Function.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{intl.formatMessage({id: `SelectField.Function.${user.job_function}`})}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.Register.Exp.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.job_experience}</div>
              </div>
            </ListItem>
            <ListItem
              title={intl.formatMessage({id: 'Profile.User.Languages.List.Title'})}
              handleEdit={handleEditLanguagesDetails}
              label={intl.formatMessage({id: 'Profile.User.Languages.Modal.Title'})}
              divider="true"
            >
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Languages.Language.Title'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.language1 ? intl.formatMessage({id: `SelectField.Lang.${user.language1}`}) : ""}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Languages.Language.Level.Title'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.language1_level ? intl.formatMessage({id: `SelectField.Level.${user.language1_level}`}) : ""}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Languages.Language.Title'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.language2 ? intl.formatMessage({id: `SelectField.Lang.${user.language2}`}) : ""}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Languages.Language.Level.Title'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.language2_level ? intl.formatMessage({id: `SelectField.Level.${user.language2_level}`}) : ""}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Languages.Language.Title'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.language3 ? intl.formatMessage({id: `SelectField.Lang.${user.language3}`}) : ""}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.User.Languages.Language.Level.Title'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.language3_level ? intl.formatMessage({id: `SelectField.Level.${user.language3_level}`}) : ""}</div>
              </div>

            </ListItem>
            <ListItem
              title={intl.formatMessage({id: 'Profile.User.Education.List.Title'})}
              handleEdit={handleEditEducationDetails}
              label={intl.formatMessage({id: 'Profile.User.Education.Modal.Title'})}
              divider="true"
            >
              <div className="my-environment-item__list my-environment-item__list-details mt-s">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Profile.Register.Education.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{intl.formatMessage({id: `SelectField.Education.${user.education}`})}</div>
              </div>
              <div className="my-environment-item__list my-environment-item__list-details mt-s l:mt-xs">
                <div className="my-environment-item__list-title text--alternative">{intl.formatMessage({id: 'Register.Form.LastSchool.Label'})}</div>
                <div className="my-environment-item__list-content mt-xxs">{user.last_school}</div>
              </div>
            </ListItem>
            <ListItem
              title={intl.formatMessage({id: 'Profile.PersonalDetails.CV.List.Title'})}
              handleEdit={handleEditCVDetails}
              label={intl.formatMessage({id: 'Profile.PersonalDetails.CV.Modal.Title'})}
              divider="true"
            >
              {userCv()}
            </ListItem>
            {process.env.REACT_APP_DSAR_REQUEST === "true" &&
                <PersonalDataInfo dsarSettings={dsarSettings} />
            }
          </ul>
        </div>
      </div>
      {renderModal()}
    </>
  )
}

export default injectIntl(ProfilePersonalDetails);
